import type { FC } from "react";
import Spacing from "@/shared/components/ui/Spacing";
import Text from "@/shared/components/ui/Text";
import { Button } from "@/shared/components/ui/Button";
import { Icon } from "@/shared/components/ui/Icon";
import type { AppError } from "@/core/errors/classes";
import layoutStyles from "@/layouts/AdminBackgroundLayout/AdminBackgroundLayout.module.scss";
import styles from "./ErrorFallback.module.scss";

const LOGO_URL =
  "https://res.cloudinary.com/dv9u0wicr/image/upload/v1745691478/SOB_Images/aceb4t4qoi1beu2gd0oi.png";

interface ErrorFallbackProps {
  error: AppError;
  onReset: () => void;
}

/**
 * Root-level ErrorBoundary fallback (see ErrorBoundaryProvider.tsx). This
 * renders OUTSIDE Redux/React Query/the Router — the crash it's catching
 * may be inside one of those providers — so it must not use
 * useAppSelector/useAppDispatch, React Query hooks, or useNavigate. Plain
 * window.location is used for navigation instead.
 */
export const ErrorFallback: FC<ErrorFallbackProps> = ({ error, onReset }) => {
  const handleReload = (): void => {
    window.location.reload();
  };

  const handleGoHome = (): void => {
    window.location.href = "/";
  };

  return (
    <Spacing className={layoutStyles.layout} display="flex" direction="column">
      <div className={layoutStyles.gradientOverlay} />

      <Spacing
        display="flex"
        justify="center"
        align="center"
        height="100vh"
        className={styles.contentLayer}
      >
        <Spacing
          display="flex"
          direction="column"
          align="center"
          width="100%"
          className={styles.wrapper}
        >
          <img src={LOGO_URL} alt="" className={styles.logo} />

          <Icon name="icon-toast-error" size={72} className={styles.icon} />

          <Text
            as="h1"
            children="Something went wrong"
            className={styles.heading}
          />
          <Text
            as="p"
            opacity={50}
            children="We hit an unexpected error. Try again, or reload the page — if it keeps happening, head back to the homepage."
            className={styles.paragraph}
          />

          {import.meta.env.DEV && (
            <Spacing className={styles.devDetails}>
              <Text
                children={error.message}
                color="error"
                fontSize={13}
                as="span"
              />
            </Spacing>
          )}

          <Spacing display="flex" gap={4} className={styles.actions}>
            <Button
              varient="solid"
              color="secondary"
              children="Try Again"
              onClick={onReset}
            />
            <Button
              varient="outline"
              color="secondary"
              children="Reload Page"
              onClick={handleReload}
            />
          </Spacing>

          <Button
            varient="link"
            color="secondary"
            children="Go to Homepage"
            onClick={handleGoHome}
            className={styles.homeLink}
          />
        </Spacing>
      </Spacing>
    </Spacing>
  );
};

export default ErrorFallback;
